import React, { useState } from 'react';
import { useRefresh, GET_MANY, useQueryWithStore, useTranslate, RecordContextProvider, ResourceContextProvider, EditController } from 'react-admin';
import { TableRow, TableCell, Collapse, IconButton, Typography, makeStyles, useMediaQuery } from '@material-ui/core'
import { KeyboardArrowUp, KeyboardArrowDown, InfoOutlined } from '@material-ui/icons';
import { sortBy } from 'lodash';

import { AssignSettingsForm } from '../../../assignSettings/AssignSettingsForm';
import { useAssignmentsContext } from './AssignmentsContext';

const getRequirement = (referee, linesperson, official, scorekeeper, timekeeper, translate) => {
  const R = translate(`resources.games.values.assignment.position_short.Referee`)
  const L = translate(`resources.games.values.assignment.position_short.Linesperson`)
  const O = translate(`resources.games.values.assignment.position_short.Official`)
  const S = translate(`resources.games.values.assignment.position_short.Scorekeeper`)
  const T = translate(`resources.games.values.assignment.position_short.Timekeeper`)

  if (referee == null && linesperson == null && official == null && scorekeeper == null && timekeeper == null) return
  if (referee === linesperson === official) return referee
  if (referee === linesperson && official == null) return referee

  const requirement = [];
  if (referee != null) {
    requirement.push(`${referee}${R}`)
  }
  if (linesperson != null) {
    requirement.push(`${linesperson}${L}`)
  }
  if (official != null) {
    requirement.push(`${official}${O}`)
  }
  if (scorekeeper != null) {
    requirement.push(`${scorekeeper}${S}`)
  }
  if (timekeeper != null) {
    requirement.push(`${timekeeper}${T}`)
  }

  return requirement.join(' / ')
}

const getSettingsSummary = (settings, offices, translate) => {
  const info = [];

  const level = getRequirement(settings.minReferee, settings.minLinesperson, settings.minOfficial, settings.minScorekeeper, settings.minTimekeeper, translate);
  if (level) {
    info.push(`${translate('resources.assignSettings.labels.required_level')}: ${level}`)
  }

  const grade = getRequirement(settings.minRefereeGrade, settings.minLinespersonGrade, null, null, null, translate);
  if (grade) {
    info.push(`${translate('resources.assignSettings.labels.required_grade')}: ${grade}`)
  }

  const age = settings.minAge;
  if (age) {
    info.push(`${translate('resources.assignSettings.labels.required_age')}: ${age}`)
  }

  if (settings.status !== 'Active') {
    info.push(settings.status)
  }

  if (offices?.length) {
    const names = sortBy(offices, office => office.id !== settings.officeId).map(office => office.name).filter(Boolean)
    if (names.length) {
      info.push(translate('resources.assignSettings.messages.assigned_by', { name: names.join(' / ') }))
    }
  }

  return info.join(' • ')
}

const useSettingsStyles = makeStyles(theme => ({
  toggleCell: {
    width: theme.spacing(22),
    whiteSpace: 'nowrap',
    borderBottom: 0,
    [theme.breakpoints.down('sm')]: {
      width: 30,
    }
  },
  cell: {
    borderBottom: 0,
  },
  icon: {
    display: 'inline',
    marginLeft: 4,
    marginRight: 10,
    verticalAlign: 'middle',
  }
}))

const Summary = ({ settings, assignments = [], isOpen, canManage, toggle }) => {
  const translate = useTranslate();
  const classes = useSettingsStyles();
  const small = useMediaQuery(theme => theme.breakpoints.down('sm'))

  const { data: offices } = useQueryWithStore({
    type: GET_MANY,
    resource: 'offices',
    payload: { ids: [settings.officeId, ...assignments.map(({ officeId }) => officeId)], _scope: 'Tenant' }
  })

  if (!canManage) {
    return <TableRow onClick={toggle}>
      <TableCell colSpan={2} className={classes.cell}>
        <div className={classes.icon}>
          <InfoOutlined fontSize="small" />
        </div>
        <Typography variant="inherit" color="textSecondary">{getSettingsSummary(settings, offices, translate)}</Typography>
      </TableCell>
    </TableRow>
  }

  return <TableRow onClick={toggle}>
    <TableCell className={classes.toggleCell}>
      <IconButton aria-label="expand row" size="small" onClick={toggle}>
        {isOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
      </IconButton>
      {!small ? ` ${translate('resources.assignSettings.name', 2)}` : ''}
    </TableCell>
    <TableCell className={classes.cell}>
      {small && <div>{translate('resources.assignSettings.name', 2)}</div>}
      {!isOpen && <Typography variant="inherit">{getSettingsSummary(settings, offices, translate)}</Typography>}
    </TableCell>
  </TableRow>
}

export default () => {
  const { game, settings, assignments, canManage } = useAssignmentsContext()
  const [ isOpen, setOpen ] = useState(false);
  const refresh = useRefresh()

  const enabled = settings && settings.enabled
  if (!enabled) return null;

  const toggle = () => {
    if (!canManage) return;
    setOpen(!isOpen);
  }
  const onClose = () => {
    setOpen(false);
  }

  const editProps = {
    basePath: '/gameAssignSettings',
    hasEdit: true,
    id: game.id,
    resource: 'gameAssignSettings',
    undoable: false,
    onSuccess() {
      onClose();
      refresh();
    },
  }
  return <>
    <Summary settings={settings} assignments={assignments} isOpen={isOpen} canManage={canManage} toggle={toggle} />
    <TableRow>
      <TableCell colSpan={2} style={{ padding: 0 }}>
        <Collapse in={isOpen}>
          {isOpen && <RecordContextProvider value={settings}>
            <ResourceContextProvider value="gameAssignSettings">
              <EditController {...editProps}>{controller =>
                <AssignSettingsForm onClose={onClose} {...controller} />
              }</EditController>
            </ResourceContextProvider>
          </RecordContextProvider>}
        </Collapse>
      </TableCell>
    </TableRow>
  </>
}
